import { looksLikeTelegramRuntime } from '@/telegram';

export type InstallGuidePlatform = 'ios' | 'android' | 'unknown';

export interface InstallEducationRuntimeSnapshot {
  platform: InstallGuidePlatform;
  isMobileBrowser: boolean;
  isStandalone: boolean;
  isTelegram: boolean;
}

export const INSTALL_SESSION_DISMISS_KEY = 'inhound:pwa-install:dismissed:session';
export const INSTALL_LOCAL_DISMISS_KEY = 'inhound:pwa-install:dismissed:until';
export const INSTALL_DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const CLUB_ROUTE_PATTERN = /^\/clubs\/[^/]+$/;
const INELIGIBLE_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/auth(?:\/|$)/,
  /^\/reader(?:\/|$)/,
  /^\/paywall(?:\/|$)/,
  /^\/legal(?:\/|$)/,
  /^\/support(?:\/|$)/,
  /^\/payments(?:\/|$)/,
  /^\/admin(?:\/|$)/,
  /^\/install(?:\/|$)/,
];
const ELIGIBLE_STATIC_ROUTES = new Set(['/catalog', '/my-clubs', '/profile']);

function getTouchPoints() {
  if (typeof window === 'undefined') {
    return 0;
  }

  return window.navigator.maxTouchPoints ?? 0;
}

export function detectInstallGuidePlatform(userAgent = '', maxTouchPoints = getTouchPoints()): InstallGuidePlatform {
  const ua = userAgent.toLowerCase();

  if (ua.includes('android')) {
    return 'android';
  }

  const isAppleMobile = /(iphone|ipod|ipad)/i.test(userAgent);
  const isIpadDesktopUa = ua.includes('macintosh') && maxTouchPoints > 1;

  if (isAppleMobile || isIpadDesktopUa) {
    return 'ios';
  }

  return 'unknown';
}

export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const standaloneMatch = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone =
    'standalone' in window.navigator &&
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  return standaloneMatch || iosStandalone;
}

export function getInstallEducationRuntimeSnapshot(): InstallEducationRuntimeSnapshot {
  if (typeof window === 'undefined') {
    return {
      platform: 'unknown',
      isMobileBrowser: false,
      isStandalone: false,
      isTelegram: false,
    };
  }

  const platform = detectInstallGuidePlatform(window.navigator.userAgent, getTouchPoints());
  const isTelegram = looksLikeTelegramRuntime();
  const isStandalone = isStandalonePwa();
  const isMobileBrowser = (platform === 'ios' || platform === 'android') && !isTelegram && !isStandalone;

  return {
    platform,
    isMobileBrowser,
    isStandalone,
    isTelegram,
  };
}

export function isEligibleInstallPromptRoute(pathname: string, initialPathname: string): boolean {
  if (INELIGIBLE_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))) {
    return false;
  }

  if (ELIGIBLE_STATIC_ROUTES.has(pathname)) {
    return true;
  }

  return pathname === initialPathname && CLUB_ROUTE_PATTERN.test(pathname);
}

export function resolveInstallGuideHref(platform: InstallGuidePlatform): '/install' | '/install/ios' | '/install/android' {
  if (platform === 'ios') {
    return '/install/ios';
  }

  if (platform === 'android') {
    return '/install/android';
  }

  return '/install';
}

export function isInstallPromptDismissedNow(now = Date.now()): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    if (window.sessionStorage.getItem(INSTALL_SESSION_DISMISS_KEY) === '1') {
      return true;
    }
  } catch {
    // Storage access is best-effort only.
  }

  try {
    const rawValue = window.localStorage.getItem(INSTALL_LOCAL_DISMISS_KEY);
    if (!rawValue) {
      return false;
    }

    const timestamp = Number(rawValue);
    return Number.isFinite(timestamp) && timestamp > now;
  } catch {
    return false;
  }
}

export function dismissInstallPrompt(now = Date.now()): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(INSTALL_SESSION_DISMISS_KEY, '1');
  } catch {
    // Storage access is best-effort only.
  }

  try {
    window.localStorage.setItem(INSTALL_LOCAL_DISMISS_KEY, String(now + INSTALL_DISMISS_WINDOW_MS));
  } catch {
    // Storage access is best-effort only.
  }
}
